/**
 * Header Value Sub-Pattern Decorator
 *
 * A CodeMirror ViewPlugin that is the SOLE source of color for HTTP header
 * values. The syntax highlighting intentionally does NOT color HeaderValue
 * nodes (they're omitted from styleTags), so this decorator has full control.
 *
 * Handles three types of headers:
 *
 * 1. Generic headers (via HeaderValue tree nodes):
 *    URLs, quoted strings, MIME types, directives, numbers
 *
 * 2. Cookie / Set-Cookie headers (via line scanning — grammar inlines these):
 *    Cookie key names, separators, plus standard patterns
 *
 * 3. Authorization headers (via line scanning — grammar inlines these):
 *    Auth type (Bearer, Basic, etc.), auth token
 *
 * Colors are passed in directly as hex values and applied via inline styles.
 * Since no syntax highlighting competes for these ranges, colors apply cleanly.
 */

import { syntaxTree, ensureSyntaxTree } from '@codemirror/language';
import { Decoration, ViewPlugin } from '@codemirror/view';
import type { DecorationSet, ViewUpdate, EditorView } from '@codemirror/view';
import type { Extension, Range } from '@codemirror/state';

export type HeaderValueColors = {
  base: string;
  number: string;
  quoted: string;
  url: string;
  mime: string;
  directive: string;
  cookieKey: string;
  separator: string;
  authType: string;
  authToken: string;
};

// =============================================================================
// Directive Words
// =============================================================================

const DIRECTIVE_WORDS = [
  'no-cache',
  'no-store',
  'must-revalidate',
  'public',
  'private',
  'immutable',
  'no-transform',
  'proxy-revalidate',
  'only-if-cached',
  'includeSubDomains',
  'preload',
  'inline',
  'attachment',
  'nosniff',
  'deny',
  'sameorigin',
  'DENY',
  'SAMEORIGIN',
  'same-origin',
  'cross-origin',
  'strict-origin-when-cross-origin',
  'strict-origin',
  'no-referrer-when-downgrade',
  'no-referrer',
  'origin-when-cross-origin',
  'unsafe-url',
  'cors',
  'no-cors',
  'navigate',
  'nested-navigate',
  'empty',
  'document',
  'close',
  'keep-alive',
  'upgrade',
  'chunked',
  'gzip',
  'deflate',
  'br',
  'zstd',
  'compress',
  'identity',
  'bytes',
  'none',
  'true',
  'false',
  'HIT',
  'MISS',
  'STALE',
  'BYPASS',
  'EXPIRED',
  'REVALIDATED',
  'DYNAMIC',
  'UPDATING',
  'Secure',
  'HttpOnly',
];

const DIRECTIVE_PATTERN = new RegExp(
  '\\b(?:' + DIRECTIVE_WORDS.join('|') + ')\\b',
  'g',
);

// =============================================================================
// Pattern Types & Factories
// =============================================================================

type PatternDef = {
  regex: RegExp;
  deco: Decoration;
};

function createStandardPatterns(colors: HeaderValueColors): PatternDef[] {
  return [
    {
      regex: /https?:\/\/[^\s,;>"']+/g,
      deco: Decoration.mark({
        attributes: {
          style: `color: ${colors.url}; text-decoration: underline`,
        },
      }),
    },
    {
      regex: /"[^"]*"/g,
      deco: Decoration.mark({
        attributes: { style: `color: ${colors.quoted}` },
      }),
    },
    {
      regex:
        /\b(?:text|application|image|audio|video|multipart|font|message|model)\/[\w.+*-]+\b/g,
      deco: Decoration.mark({
        attributes: {
          style: `color: ${colors.mime}; font-weight: 500`,
        },
      }),
    },
    {
      regex: DIRECTIVE_PATTERN,
      deco: Decoration.mark({
        attributes: { style: `color: ${colors.directive}` },
      }),
    },
    {
      regex: /\b\d+\b/g,
      deco: Decoration.mark({
        attributes: {
          style: `color: ${colors.number}; font-weight: 500`,
        },
      }),
    },
  ];
}

function createCookiePatterns(colors: HeaderValueColors): PatternDef[] {
  const cookieSpecific: PatternDef[] = [
    {
      regex: /[\w.-]+(?==)/g,
      deco: Decoration.mark({
        attributes: {
          style: `color: ${colors.cookieKey}; font-weight: 600`,
        },
      }),
    },
    {
      regex: /[=;]/g,
      deco: Decoration.mark({
        attributes: { style: `color: ${colors.separator}` },
      }),
    },
  ];

  return [...cookieSpecific, ...createStandardPatterns(colors)];
}

// =============================================================================
// Decoration Helpers
// =============================================================================

function decorateRange(
  text: string,
  baseOffset: number,
  patterns: PatternDef[],
  baseDeco: Decoration,
): Range<Decoration>[] {
  const ranges: Range<Decoration>[] = [];
  const claimed = new Uint8Array(text.length);

  for (const { regex, deco } of patterns) {
    regex.lastIndex = 0;
    let m: RegExpExecArray | null;

    while ((m = regex.exec(text)) !== null) {
      if (m[0].length === 0) {
        regex.lastIndex++;
        continue;
      }

      const start = m.index;
      const end = start + m[0].length;

      let overlap = false;
      for (let i = start; i < end; i++) {
        if (claimed[i]) {
          overlap = true;
          break;
        }
      }
      if (overlap) continue;

      for (let i = start; i < end; i++) {
        claimed[i] = 1;
      }

      ranges.push(deco.range(baseOffset + start, baseOffset + end));
    }
  }

  let runStart = -1;
  for (let i = 0; i <= text.length; i++) {
    if (i < text.length && !claimed[i]) {
      if (runStart === -1) runStart = i;
    } else {
      if (runStart !== -1) {
        ranges.push(baseDeco.range(baseOffset + runStart, baseOffset + i));
        runStart = -1;
      }
    }
  }

  return ranges;
}

// =============================================================================
// Line-Based Header Detection
// =============================================================================

const COOKIE_HEADER_RE = /^(cookie|set-cookie):\s*/i;
const AUTH_HEADER_RE = /^authorization:\s*/i;
const AUTH_TYPE_RE =
  /^(Bearer|Basic|Digest|NTLM|Negotiate|OAuth|AWS4-HMAC-SHA256)\s+/;

type LineDecoConfig = {
  standardPatterns: PatternDef[];
  cookiePatterns: PatternDef[];
  baseDeco: Decoration;
  authTypeDeco: Decoration;
  authTokenDeco: Decoration;
};

function decorateSpecialHeaders(
  view: EditorView,
  config: LineDecoConfig,
): Range<Decoration>[] {
  const ranges: Range<Decoration>[] = [];
  const doc = view.state.doc;

  for (let i = 1; i <= doc.lines; i++) {
    const line = doc.line(i);
    const lineText = line.text;

    const cookieMatch = lineText.match(COOKIE_HEADER_RE);
    if (cookieMatch) {
      const valueStart = line.from + cookieMatch[0].length;
      const valueText = lineText.slice(cookieMatch[0].length);
      if (valueText.length > 0) {
        ranges.push(
          ...decorateRange(
            valueText,
            valueStart,
            config.cookiePatterns,
            config.baseDeco,
          ),
        );
      }
      continue;
    }

    const authMatch = lineText.match(AUTH_HEADER_RE);
    if (authMatch) {
      const valueStart = line.from + authMatch[0].length;
      const valueText = lineText.slice(authMatch[0].length);
      if (valueText.length > 0) {
        const typeMatch = valueText.match(AUTH_TYPE_RE);
        if (typeMatch) {
          const typeEnd = valueStart + typeMatch[1].length;
          ranges.push(config.authTypeDeco.range(valueStart, typeEnd));

          const tokenStart = valueStart + typeMatch[0].length;
          const tokenText = valueText.slice(typeMatch[0].length);
          if (tokenText.length > 0) {
            ranges.push(
              config.authTokenDeco.range(
                tokenStart,
                line.from + lineText.length,
              ),
            );
          }
        } else {
          ranges.push(
            config.baseDeco.range(valueStart, line.from + lineText.length),
          );
        }
      }
      continue;
    }
  }

  return ranges;
}

// =============================================================================
// Build All Decorations
// =============================================================================

function buildDecorations(
  view: EditorView,
  config: LineDecoConfig,
): DecorationSet {
  const tree =
    ensureSyntaxTree(view.state, view.state.doc.length, 500) ??
    syntaxTree(view.state);

  const ranges: Range<Decoration>[] = [];

  // 1. Handle generic headers via tree nodes
  tree.iterate({
    enter(node) {
      if (node.name === 'HeaderValue') {
        const text = view.state.sliceDoc(node.from, node.to);
        ranges.push(
          ...decorateRange(
            text,
            node.from,
            config.standardPatterns,
            config.baseDeco,
          ),
        );
      }
    },
  });

  // 2. Handle cookie & auth headers via line scanning
  // (these are inlined by the Lezer compiler and produce no named nodes)
  ranges.push(...decorateSpecialHeaders(view, config));

  return Decoration.set(ranges, true);
}

// =============================================================================
// Export
// =============================================================================

export function headerValueDecorator(colors: HeaderValueColors): Extension {
  const config: LineDecoConfig = {
    standardPatterns: createStandardPatterns(colors),
    cookiePatterns: createCookiePatterns(colors),
    baseDeco: Decoration.mark({
      attributes: { style: `color: ${colors.base}` },
    }),
    authTypeDeco: Decoration.mark({
      attributes: {
        style: `color: ${colors.authType}; font-weight: 700`,
      },
    }),
    authTokenDeco: Decoration.mark({
      attributes: {
        style: `color: ${colors.authToken}; font-style: italic`,
      },
    }),
  };

  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = buildDecorations(view, config);
      }

      update(update: ViewUpdate) {
        if (
          update.docChanged ||
          update.viewportChanged ||
          syntaxTree(update.state) !== syntaxTree(update.startState)
        ) {
          this.decorations = buildDecorations(update.view, config);
        }
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );
}
